import {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from './supabase'
import * as Sentry from "@sentry/react";
import { identifyUser, resetAnalytics } from './analytics'

export interface Profile {
    id: string
    full_name: string | null
    business_name: string | null
    phone: string | null
    location: string | null
    business_category: string | null
    trial_started_at: string | null
    notification_prefs: {
        daily_summary?: boolean
        unpaid_reminders?: boolean
        trial_warnings?: boolean
        sunday_insights?: boolean
        streaks?: boolean
    } | null
    delivery_rates?: { zone: string; price: number }[] | null
    free_delivery_minimum?: number | null
    bank_name: string | null
    bank_account_number: string | null
    bank_account_name: string | null
    slug?: string | null
    avatar_url?: string | null
    banner_url?: string | null
    tagline?: string | null
    description?: string | null
    category?: string | null
    public_whatsapp?: string | null
    instagram_handle?: string | null
    tiktok_handle?: string | null
    twitter_handle?: string | null
    facebook_url?: string | null
    youtube_url?: string | null
    google_maps_url?: string | null
    announcement_text?: string | null
    personal_avatar_url?: string | null
    is_profile_visible?: boolean
    is_verified?: boolean
    current_streak?: number
    longest_streak?: number
    last_order_date?: string
    streak_updated_at?: string
    email?: string
    created_at?: string
    service_availability?: any
    currency?: 'NGN' | 'GHS' | 'KES' | 'XOF'
    country?: string | null
    peer_credit_score?: number
    cnpl_auto_approve_threshold?: number | null
    business_reg_number?: string | null
    business_reg_type?: string | null
    business_reg_date?: string | null
    business_reg_verified?: boolean
    testimonials?: { id: string; name: string; role: string; content: string; rating: number }[] | null
    faqs?: { id: string; question: string; answer: string }[] | null
    peer_credit_score?: number | null
    credit_limit?: number | null
    brand_color_primary?: string | null
    brand_color_secondary?: string | null
    shipping_logic?: any | null
    custom_domain?: string | null
}

interface AuthState {
    user: User | null
    session: Session | null
    profile: Profile | null
    userRole: 'owner' | 'admin' | 'manager' | 'cashier' | 'dispatcher' | null
    loading: boolean
}

interface AuthActions {
    signIn: (email: string, password: string) => Promise<{ error: string | null }>
    signUp: (
        email: string,
        password: string,
        profileData: Omit<Profile, 'id' | 'created_at'>
    ) => Promise<{ error: string | null }>
    signInWithGoogle: () => Promise<{ error: string | null }>
    signOut: () => Promise<void>
    refreshProfile: () => Promise<void>
    sendResetLink: (email: string) => Promise<{ error: string | null }>
    updatePassword: (password: string) => Promise<{ error: string | null }>
}

type AuthContextType = AuthState & AuthActions

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [session, setSession] = useState<Session | null>(null)
    const [profile, setProfile] = useState<Profile | null>(null)
    const [userRole, setUserRole] = useState<'owner' | 'admin' | 'manager' | 'cashier' | 'dispatcher' | null>(null)
    const [loading, setLoading] = useState(true)

    const fetchProfile = async (userId: string, email?: string | null) => {
        try {
            // Find staff by user_id OR by email (if they just signed up via invite)
            // We use the known user email from the session if available to claim it.
            const userEmail = email || session?.user?.email || (await supabase.auth.getUser()).data.user?.email;

            let staffData = null;

            if (userEmail) {
                const { data } = await supabase
                    .from('staff_members')
                    .select('id, seller_id, role, user_id')
                    .or(`user_id.eq.${userId},email.eq.${userEmail}`)
                    .maybeSingle()

                staffData = data;

                // Auto-link newly registered invited staff
                if (staffData && !staffData.user_id) {
                    await supabase.from('staff_members').update({ user_id: userId }).eq('id', staffData.id);
                }
            } else {
                const { data } = await supabase
                    .from('staff_members')
                    .select('seller_id, role')
                    .eq('user_id', userId)
                    .maybeSingle()
                staffData = data;
            }

            const activeProfileId = staffData ? staffData.seller_id : userId
            const role = staffData ? staffData.role : 'owner'

            setUserRole(role as any)

            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', activeProfileId)
                .single()
            if (!error && data) {
                setProfile(data as Profile)
                identifyUser(userId, {
                    business_name: data.business_name,
                    category: data.category || data.business_category,
                    location: data.location,
                    is_verified: data.is_verified,
                    role: role
                })

                // Identify user in Sentry
                Sentry.setUser({
                    id: userId,
                    email: data.email || undefined,
                    username: data.business_name || undefined,
                });
            }
        } catch (err) {
            console.error('[Auth] Error fetching profile:', err);
        }
    }

    useEffect(() => {
        // Get initial session
        supabase.auth.getSession().then(({ data: { session }, error }) => {
            if (error) {
                console.warn('[Auth] Initial session recovery failed:', error.message)
                // If the refresh token is invalid/not found, clear the local state
                // to prevent "zombie" sessions and allow user to log in again.
                if (error.message.toLowerCase().includes('refresh_token')) {
                    setSession(null)
                    setUser(null)
                    setProfile(null)
                }
                setLoading(false)
            } else {
                setSession(session)
                setUser(session?.user ?? null)
                if (session?.user) {
                    fetchProfile(session.user.id, session.user.email).finally(() => {
                        setLoading(false)
                    })
                } else {
                    setLoading(false)
                }
            }
        })

        // Listen to auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (_event, session) => {
                setSession(session)
                setUser(session?.user ?? null)
                if (session?.user) {
                    try {
                        await fetchProfile(session.user.id, session.user.email)
                    } catch (err) {
                        console.error('[Auth] Error during auth state change profile fetch:', err)
                    }
                } else {
                    setProfile(null)
                }
                setLoading(false)
            }
        )

        return () => subscription.unsubscribe()
    }, [])

    const lastVisibleAt = useRef(Date.now())

    // ── Smart Session Resumption ─────────────────────────────────────────────
    // Detects when the user returns to the app (after being backgrounded or 
    // inactive) and automatically refreshes data if they were away for >5min.
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                const now = Date.now()
                const elapsedMin = (now - lastVisibleAt.current) / (1000 * 60)

                if (elapsedMin >= 5) {
                    console.log(`[Auth] Smart Resumption: Refreshing (away for ${Math.round(elapsedMin)}m)`)
                    // Hard refresh: session check + profile re-fetch
                    const { data: { session: currentSession } } = await supabase.auth.getSession()
                    if (currentSession?.user) {
                        await fetchProfile(currentSession.user.id)
                    }
                } else if (user) {
                    // Soft refresh: background session check
                    const { data: { session: currentSession } } = await supabase.auth.getSession()
                    if (!currentSession) {
                        console.log('[Auth] Smart Resumption: Session expired')
                        setSession(null)
                        setUser(null)
                        setProfile(null)
                    }
                }
            } else {
                lastVisibleAt.current = Date.now()
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('focus', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('focus', handleVisibilityChange)
        }
    }, [user, profile])

    const signIn = async (email: string, password: string) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) return { error: error.message }
        return { error: null }
    }

    const signUp = async (
        email: string,
        password: string,
        profileData: Omit<Profile, 'id' | 'created_at'>
    ) => {
        const { error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    full_name: profileData.full_name,
                    business_name: profileData.business_name,
                    phone: profileData.phone,
                    location: profileData.location,
                    business_category: profileData.business_category || profileData.category,
                    category: profileData.category || profileData.business_category,
                },
            },
        })
        if (error) return { error: error.message }
        return { error: null }
    }

    const signInWithGoogle = async () => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: `${window.location.origin}/seller`,
            },
        })
        if (error) return { error: error.message }
        return { error: null }
    }

    const signOut = async () => {
        try {
            // Manual cleanup of the Supabase storage key as a safety measure
            localStorage.removeItem('moniflow-auth');

            await supabase.auth.signOut();

            // Manually clear state to ensure UI updates immediately
            setSession(null);
            setUser(null);
            setProfile(null);
            setUserRole(null);
            resetAnalytics();
            Sentry.setUser(null);

        } catch (error) {
            console.error('[Auth] Logout error:', error);
            // Even if the network call fails, we should clear local state to "force" logout
            localStorage.removeItem('moniflow-auth');
            setSession(null);
            setUser(null);
            setProfile(null);
            setUserRole(null);
        }
    };

    const refreshProfile = async () => {
        // Use cached user ID if available to skip an extra network request
        const userId = user?.id;
        if (userId) {
            await fetchProfile(userId);
        } else {
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            if (currentUser) await fetchProfile(currentUser.id);
        }
    }

    const sendResetLink = async (email: string) => {
        const { error } = await supabase.functions.invoke('send-reset-email', {
            body: { email }
        })
        if (error) return { error: error.message }
        return { error: null }
    }

    const updatePassword = async (password: string) => {
        const { error } = await supabase.auth.updateUser({ password })
        if (error) return { error: error.message }
        return { error: null }
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                session,
                profile,
                userRole,
                loading,
                signIn,
                signUp,
                signInWithGoogle,
                signOut,
                refreshProfile,
                sendResetLink,
                updatePassword
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
    return ctx
}
